import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="search-autocomplete"
export default class extends Controller {
  static targets = ["input", "suggestions", "hiddenInput"]
  static values = { 
    url: String,
    minLength: { type: Number, default: 2 }
  }

  connect() {
    this.currentFocus = -1
    this.debounceTimer = null
    this.cache = new Map()
  }

  disconnect() {
    this.clearDebounce()
    this.hideSuggestions()
  }

  inputChanged(event) {
    this.clearDebounce()
    const query = event.target.value.trim()

    if (query.length < this.minLengthValue) {
      this.hideSuggestions()
      return
    }

    // Debounce API calls to avoid excessive requests
    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(query)
    }, 300)
  }

  async fetchSuggestions(query) {
    try {
      // Check cache first
      if (this.cache.has(query)) {
        this.displaySuggestions(this.cache.get(query))
        return
      }

      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const suggestions = await response.json()
      
      // Cache the results
      this.cache.set(query, suggestions)
      
      this.displaySuggestions(suggestions)
    } catch (error) {
      console.error('Failed to fetch suggestions:', error)
      this.hideSuggestions()
    }
  }

  displaySuggestions(suggestions) {
    if (!suggestions || suggestions.length === 0) {
      this.hideSuggestions()
      return
    }

    let html = ''
    
    // Group suggestions by type
    const products = suggestions.filter(s => s.type === 'product')
    const categories = suggestions.filter(s => s.type === 'category')

    if (products.length > 0) {
      products.forEach((product, index) => {
        html += `
          <div class="suggestion-item" data-index="${index}" data-url="${product.url}">
            <div class="suggestion-content">
              <span class="suggestion-title">${product.title}</span>
              <span class="suggestion-price">${product.price}€</span>
            </div>
          </div>
        `
      })
    }

    if (categories.length > 0) {
      categories.forEach((category) => {
        html += `
          <div class="suggestion-item" data-url="${category.url}">
            <div class="suggestion-content">
              <span class="suggestion-title">${category.title}</span>
            </div>
          </div>
        `
      })
    }

    this.suggestionsTarget.innerHTML = html
    this.showSuggestions()
    this.bindSuggestionEvents()
  }

  bindSuggestionEvents() {
    const items = this.suggestionsTarget.querySelectorAll('.suggestion-item')
    
    items.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault()
        this.selectSuggestion(item)
      })

      item.addEventListener('mouseenter', () => {
        this.setActiveSuggestion(index)
      })
    })
  }

  selectSuggestion(item) {
    const url = item.dataset.url
    if (url) {
      window.location.href = url
    }
  }

  keydownHandler(event) {
    const items = this.suggestionsTarget.querySelectorAll('.suggestion-item')
    
    if (items.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.currentFocus = Math.min(this.currentFocus + 1, items.length - 1)
        this.setActiveSuggestion(this.currentFocus)
        break
        
      case 'ArrowUp':
        event.preventDefault()
        this.currentFocus = Math.max(this.currentFocus - 1, -1)
        this.setActiveSuggestion(this.currentFocus)
        break
        
      case 'Enter':
        event.preventDefault()
        if (this.currentFocus >= 0 && items[this.currentFocus]) {
          this.selectSuggestion(items[this.currentFocus])
        }
        break
        
      case 'Escape':
        this.hideSuggestions()
        this.inputTarget.blur()
        break
    }
  }

  setActiveSuggestion(index) {
    const items = this.suggestionsTarget.querySelectorAll('.suggestion-item')
    
    items.forEach((item, i) => {
      if (i === index) {
        item.classList.add('active')
      } else {
        item.classList.remove('active')
      }
    })
    
    this.currentFocus = index
  }

  showSuggestions() {
    this.suggestionsTarget.classList.add('show')
    this.currentFocus = -1
  }

  hideSuggestions() {
    this.suggestionsTarget.classList.remove('show')
    this.suggestionsTarget.innerHTML = ''
    this.currentFocus = -1
  }

  // Handle clicks outside the autocomplete area
  documentClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }

  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  // Event listeners for input focus and blur
  inputFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length >= this.minLengthValue && this.cache.has(query)) {
      this.displaySuggestions(this.cache.get(query))
    }
  }

  inputBlur(event) {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.hideSuggestions()
      }
    }, 150)
  }
}
